import React, { useState, useCallback } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';
import '../../../styles/dev-utils-theme.css';
import { 
  FileText, 
  Copy, 
  Download, 
  RefreshCw, 
  Check, 
  AlertCircle,
  Minimize2,
  Maximize2,
  GitCompare,
  RotateCcw
} from 'lucide-react';

function JsonFormatter() {
  const [activeTab, setActiveTab] = useState<'formatter' | 'comparator'>('formatter');
  const [inputJson, setInputJson] = useState('');
  const [outputJson, setOutputJson] = useState('');
  const [error, setError] = useState('');
  const [isValidJson, setIsValidJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const [indentSize, setIndentSize] = useState(2);
  const [sortKeys, setSortKeys] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  
  // Comparator state
  const [json1, setJson1] = useState('');
  const [json2, setJson2] = useState('');
  const [compareResult, setCompareResult] = useState<any>(null);
  const [compareError, setCompareError] = useState('');
  const [highlightedJson1, setHighlightedJson1] = useState('');
  const [highlightedJson2, setHighlightedJson2] = useState('');

  const sortObjectKeys = useCallback((obj: any): any => {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(sortObjectKeys);
    }
    
    const sortedKeys = Object.keys(obj).sort();
    const sortedObj: any = {};
    
    sortedKeys.forEach(key => {
      sortedObj[key] = sortObjectKeys(obj[key]);
    });
    
    return sortedObj;
  }, []);

  const findDifferences = useCallback((obj1: any, obj2: any, path: string): any[] => {
    const differences: any[] = [];
    
    // Handle null/undefined cases
    if (obj1 === null || obj1 === undefined) {
      if (obj2 !== null && obj2 !== undefined) {
        differences.push({ type: 'added', path, value: obj2 });
      }
      return differences;
    }
    
    if (obj2 === null || obj2 === undefined) {
      differences.push({ type: 'removed', path, value: obj1 });
      return differences;
    }
    
    // Handle primitive types
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      if (obj1 !== obj2) {
        differences.push({ type: 'modified', path, oldValue: obj1, newValue: obj2 });
      }
      return differences;
    }
    
    // Handle arrays
    if (Array.isArray(obj1) || Array.isArray(obj2)) {
      if (!Array.isArray(obj1)) {
        differences.push({ type: 'modified', path, oldValue: obj1, newValue: obj2 });
        return differences;
      }
      if (!Array.isArray(obj2)) {
        differences.push({ type: 'modified', path, oldValue: obj1, newValue: obj2 });
        return differences;
      }
      
      const maxLength = Math.max(obj1.length, obj2.length);
      for (let i = 0; i < maxLength; i++) {
        const newPath = `${path}[${i}]`;
        if (i >= obj1.length) {
          differences.push({ type: 'added', path: newPath, value: obj2[i] });
        } else if (i >= obj2.length) {
          differences.push({ type: 'removed', path: newPath, value: obj1[i] });
        } else {
          differences.push(...findDifferences(obj1[i], obj2[i], newPath));
        }
      }
      return differences;
    }
    
    // Handle objects
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
    
    for (const key of allKeys) {
      const newPath = path ? `${path}.${key}` : key;
      
      if (!(key in obj1)) {
        differences.push({ type: 'added', path: newPath, value: obj2[key] });
      } else if (!(key in obj2)) {
        differences.push({ type: 'removed', path: newPath, value: obj1[key] });
      } else {
        differences.push(...findDifferences(obj1[key], obj2[key], newPath));
      }
    }
    
    return differences;
  }, []);

  const highlightJsonDifferences = useCallback((jsonString: string, differences: any[], isFirst: boolean) => {
    if (!jsonString || !differences || differences.length === 0) {
      return jsonString;
    }

    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      const lines = formatted.split('\n');
      
      // Simple approach: highlight lines that contain changed paths
      const changedPaths = differences.map(diff => diff.path);
      
      const highlightedLines = lines.map(line => {
        const trimmedLine = line.trim();
        
        // Check if this line contains a changed property
        const hasChange = changedPaths.some(path => {
          if (!path) return false;
          
          // Extract the key from the path (last part after the last dot)
          const key = path.includes('.') ? path.split('.').pop() : path;
          
          // Check if the line contains this key as a property
          const keyPattern = new RegExp(`"${key}"\\s*:`);
          return keyPattern.test(trimmedLine);
        });
        
        if (hasChange) {
          // Find the type of change for this line
          const relevantDiff = differences.find(diff => {
            if (!diff.path) return false;
            const key = diff.path.includes('.') ? diff.path.split('.').pop() : diff.path;
            const keyPattern = new RegExp(`"${key}"\\s*:`);
            return keyPattern.test(trimmedLine);
          });
          
          if (relevantDiff) {
            let highlightClass = '';
            
            if (isFirst) {
              // For the first JSON (original)
              if (relevantDiff.type === 'removed') highlightClass = 'diff-removed';
              else if (relevantDiff.type === 'modified') highlightClass = 'diff-modified';
            } else {
              // For the second JSON (modified)
              if (relevantDiff.type === 'added') highlightClass = 'diff-added';
              else if (relevantDiff.type === 'modified') highlightClass = 'diff-modified';
            }
            
            if (highlightClass) {
              return `__HIGHLIGHT_START_${highlightClass}__${line}__HIGHLIGHT_END__`;
            }
          }
        }
        
        return line;
      });
      
      return highlightedLines.join('\n');
    } catch (error) {
      return jsonString;
    }
  }, []);

  const createHighlightedHtml = useCallback((content: string) => {
    // Escape HTML characters first
    let result = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Then apply our custom highlighting markers
    result = result
      .replace(/__HIGHLIGHT_START_diff-added__(.*?)__HIGHLIGHT_END__/g, '<div style="background-color: var(--lyra-color-success-bg); color: var(--lyra-color-success); margin: 0; padding: 0 4px; border-left: 3px solid var(--lyra-color-success);">$1</div>')
      .replace(/__HIGHLIGHT_START_diff-removed__(.*?)__HIGHLIGHT_END__/g, '<div style="background-color: var(--lyra-color-danger-bg); color: var(--lyra-color-danger); margin: 0; padding: 0 4px; border-left: 3px solid var(--lyra-color-danger);">$1</div>')
      .replace(/__HIGHLIGHT_START_diff-modified__(.*?)__HIGHLIGHT_END__/g, '<div style="background-color: var(--lyra-color-surface-2); color: #ca8a04; margin: 0; padding: 0 4px; border-left: 3px solid #eab308;">$1</div>');
    
    return result;
  }, []);

  const formatJson = useCallback(() => {
    if (!inputJson.trim()) {
      setOutputJson('');
      setError('');
      setIsValidJson(false);
      return;
    }

    try {
      // Parse the JSON to validate it
      const parsed = JSON.parse(inputJson);
      
      // Sort keys if option is enabled
      const processedData = sortKeys ? sortObjectKeys(parsed) : parsed;
      
      // Format with specified indentation or compact
      const formatted = isCompact 
        ? JSON.stringify(processedData)
        : JSON.stringify(processedData, null, indentSize);
      
      setOutputJson(formatted);
      setError('');
      setIsValidJson(true);
    } catch (err: any) {
      setError(`Invalid JSON: ${err.message}`);
      setOutputJson('');
      setIsValidJson(false);
    }
  }, [inputJson, indentSize, sortKeys, isCompact, sortObjectKeys]);

  // Compare JSON objects
  const compareJsonObjects = useCallback(() => {
    setCompareError('');
    setCompareResult(null);
    setHighlightedJson1('');
    setHighlightedJson2('');

    if (!json1.trim() || !json2.trim()) {
      setCompareError('Both JSON inputs are required for comparison');
      return;
    }

    try {
      const parsed1 = JSON.parse(json1);
      const parsed2 = JSON.parse(json2);
      
      const differences = findDifferences(parsed1, parsed2, '');
      const isEqual = differences.length === 0;
      
      // Generate highlighted versions
      const formatted1 = JSON.stringify(parsed1, null, 2);
      const formatted2 = JSON.stringify(parsed2, null, 2);
      
      const highlighted1 = highlightJsonDifferences(formatted1, differences, true);
      const highlighted2 = highlightJsonDifferences(formatted2, differences, false);
      
      setHighlightedJson1(highlighted1);
      setHighlightedJson2(highlighted2);
      
      setCompareResult({
        isEqual,
        differences,
        summary: {
          total: differences.length,
          added: differences.filter(d => d.type === 'added').length,
          removed: differences.filter(d => d.type === 'removed').length,
          modified: differences.filter(d => d.type === 'modified').length
        }
      });
    } catch (err: any) {
      setCompareError(`Invalid JSON: ${err.message}`);
    }
  }, [json1, json2, findDifferences, highlightJsonDifferences]);

  // Auto-format when input changes (formatter mode)
  React.useEffect(() => {
    if (activeTab === 'formatter') {
      const timeoutId = setTimeout(() => {
        formatJson();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [formatJson, activeTab]);

  // Auto-compare when inputs change (comparator mode)
  React.useEffect(() => {
    if (activeTab === 'comparator') {
      const timeoutId = setTimeout(() => {
        compareJsonObjects();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [compareJsonObjects, activeTab]);

  const copyToClipboard = async (text?: string) => {
    const textToCopy = text || outputJson;
    if (!textToCopy) return;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadJson = () => {
    if (!outputJson) return;
    
    const blob = new Blob([outputJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    if (activeTab === 'formatter') {
      setInputJson('');
      setOutputJson('');
      setError('');
      setIsValidJson(false);
    } else {
      setJson1('');
      setJson2('');
      setCompareResult(null);
      setCompareError('');
      setHighlightedJson1('');
      setHighlightedJson2('');
    }
  };

  const loadSampleJson = () => {
    const sampleJson = {
      "name": "John Doe",
      "age": 30,
      "email": "john.doe@example.com",
      "address": {
        "street": "123 Main St",
        "city": "New York",
        "zipcode": "10001",
        "coordinates": {
          "lat": 40.7128,
          "lng": -74.0060
        }
      },
      "hobbies": ["reading", "coding", "traveling"],
      "isActive": true,
      "lastLogin": "2024-08-09T10:30:00Z"
    };
    
    if (activeTab === 'formatter') {
      setInputJson(JSON.stringify(sampleJson));
    } else {
      const sampleJson2 = {
        ...sampleJson,
        age: 31,
        email: "john.smith@example.com",
        address: {
          ...sampleJson.address,
          city: "Boston"
        },
        hobbies: ["reading", "coding", "traveling", "photography"],
        lastLogin: "2024-08-10T09:15:00Z"
      };
      setJson1(JSON.stringify(sampleJson, null, 2));
      setJson2(JSON.stringify(sampleJson2, null, 2));
    }
  };

  const getLineNumbers = (text: string) => {
    const lines = text.split('\n');
    return lines.map((_, index) => (
      <div key={index} className="text-gray-400 text-sm select-none pr-3 text-right">
        {index + 1}
      </div>
    ));
  };

  return (
    <div className="dev-utils-page" style={{ minHeight: "100vh", background: "var(--lyra-color-surface-2)", fontFamily: "'DM Sans', system-ui, sans-serif", padding: "28px 28px 48px" }}>
      <PageTracker pageTitle="JSON Formatter" category="dev-utils" />
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <FileText size={20} style={{ color: "var(--app-primary-mid)" }} />
          <span style={{ fontSize: 22, fontWeight: 600, color: "var(--lyra-color-text)", fontFamily: "DM Sans, system-ui, sans-serif" }}>JSON Formatter & Comparator</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--app-text-2)", margin: 0 }}>Format, validate, beautify, and compare JSON data with customizable options</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('formatter')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'formatter'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText size={18} />
                JSON Formatter
              </div>
            </button>
            <button
              onClick={() => setActiveTab('comparator')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'comparator'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <GitCompare size={18} />
                JSON Comparator
              </div>
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'formatter' ? (
        // Formatter Content
        <>
          {/* Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Indent Size:
                </label>
                <select
                  value={indentSize}
                  onChange={(e) => setIndentSize(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value={2}>2 spaces</option>
                  <option value={4}>4 spaces</option>
                  <option value={8}>8 spaces</option>
                </select>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sortKeys}
                  onChange={(e) => setSortKeys(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort Keys</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isCompact}
                  onChange={(e) => setIsCompact(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Compact Mode</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showLineNumbers}
                  onChange={(e) => setShowLineNumbers(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Line Numbers</span>
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={loadSampleJson}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <FileText size={16} />
                Load Sample
              </button>

              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <RefreshCw size={16} />
                Clear All
              </button>

              {outputJson && (
                <>
                  <button
                    onClick={() => copyToClipboard()}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy Output'}
                  </button>

                  <button
                    onClick={downloadJson}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Status */}
          {(error || isValidJson) && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              error 
                ? 'bg-red-50 border border-red-200 text-red-700' 
                : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              {error ? (
                <AlertCircle size={20} />
              ) : (
                <Check size={20} />
              )}
              <div>
                {error || 'Valid JSON - Successfully formatted!'}
              </div>
            </div>
          )}

          {/* Editor Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText size={20} />
                  Input JSON
                </h2>
              </div>
              <div className="p-4">
                <div className="relative">
                  <textarea
                    value={inputJson}
                    onChange={(e) => setInputJson(e.target.value)}
                    placeholder="Paste your JSON here..."
                    className="w-full h-96 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white resize-none"
                    style={{ 
                      paddingLeft: showLineNumbers ? '60px' : '16px',
                      lineHeight: '1.5'
                    }}
                  />
                  {showLineNumbers && inputJson && (
                    <div className="absolute left-4 top-4 flex flex-col font-mono text-sm">
                      {getLineNumbers(inputJson)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Output */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText size={20} />
                  Formatted Output
                </h2>
              </div>
              <div className="p-4">
                <div className="relative">
                  <textarea
                    value={outputJson}
                    readOnly
                    placeholder="Formatted JSON will appear here..."
                    className="w-full h-96 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-700 dark:text-white resize-none"
                    style={{ 
                      paddingLeft: showLineNumbers && outputJson ? '60px' : '16px',
                      lineHeight: '1.5'
                    }}
                  />
                  {showLineNumbers && outputJson && (
                    <div className="absolute left-4 top-4 flex flex-col font-mono text-sm">
                      {getLineNumbers(outputJson)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* JSON Info */}
          {outputJson && (
            <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">JSON Statistics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-gray-900 dark:text-white">Characters</div>
                  <div className="text-gray-600 dark:text-gray-400">{outputJson.length.toLocaleString()}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-gray-900 dark:text-white">Lines</div>
                  <div className="text-gray-600 dark:text-gray-400">{outputJson.split('\n').length}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-gray-900 dark:text-white">Size</div>
                  <div className="text-gray-600 dark:text-gray-400">
                    {(new Blob([outputJson]).size / 1024).toFixed(2)} KB
                  </div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-gray-900 dark:text-white">Status</div>
                  <div className="text-green-600 dark:text-green-400">Valid JSON</div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // Comparator Content
        <>
          {/* Comparator Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={loadSampleJson}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <GitCompare size={16} />
                Load Sample Comparison
              </button>

              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <RotateCcw size={16} />
                Clear All
              </button>

              {compareResult && (
                <button
                  onClick={() => copyToClipboard(JSON.stringify(compareResult, null, 2))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
                    copied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy Results'}
                </button>
              )}
            </div>
          </div>

          {/* Comparison Status */}
          {(compareError || compareResult) && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              compareError 
                ? 'bg-red-50 border border-red-200 text-red-700' 
                : compareResult?.isEqual
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            }`}>
              {compareError ? (
                <AlertCircle size={20} />
              ) : compareResult?.isEqual ? (
                <Check size={20} />
              ) : (
                <GitCompare size={20} />
              )}
              <div>
                {compareError || (compareResult?.isEqual ? 'JSON objects are identical!' : `Found ${compareResult?.summary.total} differences`)}
              </div>
            </div>
          )}

          {/* Comparison Legend */}
          {compareResult && compareResult.differences.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Color Legend:</h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border-l-4 border-green-500 rounded-sm"></div>
                  <span className="text-gray-700 dark:text-gray-300">Added in JSON 2</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 border-l-4 border-red-500 rounded-sm"></div>
                  <span className="text-gray-700 dark:text-gray-300">Removed from JSON 1</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-100 border-l-4 border-yellow-500 rounded-sm"></div>
                  <span className="text-gray-700 dark:text-gray-300">Modified value</span>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Inputs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* JSON 1 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText size={20} />
                  JSON 1 (Original)
                </h2>
              </div>
              <div className="p-4">
                <textarea
                  value={json1}
                  onChange={(e) => setJson1(e.target.value)}
                  placeholder="Paste first JSON here..."
                  className="w-full h-48 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white resize-none"
                />
                {highlightedJson1 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Formatted with Differences:
                    </div>
                    <div 
                      className="w-full h-48 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-700 dark:text-white overflow-auto whitespace-pre"
                      style={{ lineHeight: '1.5' }}
                      dangerouslySetInnerHTML={{ 
                        __html: createHighlightedHtml(highlightedJson1)
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* JSON 2 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText size={20} />
                  JSON 2 (Modified)
                </h2>
              </div>
              <div className="p-4">
                <textarea
                  value={json2}
                  onChange={(e) => setJson2(e.target.value)}
                  placeholder="Paste second JSON here..."
                  className="w-full h-48 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm dark:bg-gray-700 dark:text-white resize-none"
                />
                {highlightedJson2 && (
                  <div className="mt-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Formatted with Differences:
                    </div>
                    <div 
                      className="w-full h-48 p-4 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-gray-50 dark:bg-gray-700 dark:text-white overflow-auto whitespace-pre"
                      style={{ lineHeight: '1.5' }}
                      dangerouslySetInnerHTML={{ 
                        __html: createHighlightedHtml(highlightedJson2)
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Comparison Results */}
          {compareResult && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <GitCompare size={20} />
                Comparison Results
              </h3>
              
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="font-semibold text-gray-900 dark:text-white">Total Changes</div>
                  <div className="text-gray-600 dark:text-gray-400">{compareResult.summary.total}</div>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="font-semibold text-green-700 dark:text-green-300">Added</div>
                  <div className="text-green-600 dark:text-green-400">{compareResult.summary.added}</div>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="font-semibold text-red-700 dark:text-red-300">Removed</div>
                  <div className="text-red-600 dark:text-red-400">{compareResult.summary.removed}</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div className="font-semibold text-yellow-700 dark:text-yellow-300">Modified</div>
                  <div className="text-yellow-600 dark:text-yellow-400">{compareResult.summary.modified}</div>
                </div>
              </div>

              {/* Differences List */}
              {compareResult.differences.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Detailed Changes:</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {compareResult.differences.map((diff: any, index: number) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg text-sm font-mono ${
                          diff.type === 'added'
                            ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-200'
                            : diff.type === 'removed'
                            ? 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-200'
                            : 'bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-200'
                        }`}
                      >
                        <div className="font-semibold mb-1">
                          {diff.type.toUpperCase()}: {diff.path || 'root'}
                        </div>
                        {diff.type === 'modified' ? (
                          <div>
                            <div>- {JSON.stringify(diff.oldValue)}</div>
                            <div>+ {JSON.stringify(diff.newValue)}</div>
                          </div>
                        ) : (
                          <div>
                            {diff.type === 'added' ? '+ ' : '- '}
                            {JSON.stringify(diff.value)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default JsonFormatter;
